// @ts-nocheck

import { SelectOptionType } from '@/types';
import LocalStorageHelper from '@/helpers/global/localStorage.helper';

class GlobalHelper {
  generateHashFromString(str: string) {
    return str.split('').reduce(function (a, b) {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
  }

  getUrlQueryParams() {
    const urlQueryParams = new URLSearchParams(window.location.search);
    const urlQueryParamsObj = Object.fromEntries(urlQueryParams.entries());
    return JSON.parse(JSON.stringify(urlQueryParamsObj));
  }

  getUrlQueryParameter(paramName: string): string {
    const urlParams = new URLSearchParams(window.location.search);

    return urlParams.get(paramName);
  }

  setUrlQueryParameter(paramName: string, paramValue: string) {
    const url = new URL(window.location);
    url.searchParams.set(paramName, paramValue);
    window.history.pushState({}, '', url);
  }

  paramsToQueryString(params: { string: any }) {
    let queryParamsString = '';
    if (params !== undefined && Object.keys(params).length > 0) {
      let isFistIteration = true;
      Object.keys(params).forEach((key) => {
        queryParamsString += `${(isFistIteration ? '?' : '&') + key}=${params[key]}`;
        isFistIteration = false;
      });
    }
    return queryParamsString;
  }

  slugify(string: string) {
    const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
    const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
    const p = new RegExp(a.split('').join('|'), 'g');

    return string
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, ''); // Trim - from end of text
  }

  convertFloatNumberToFraction(number: number) {
    const gcd = function (a, b) {
      if (b < 0.0000001) return a;

      return gcd(b, Math.floor(a % b));
    };

    const len = number.toString().length - 2;

    let denominator = Math.pow(10, len);
    let numerator = number * denominator;

    const divisor = gcd(numerator, denominator);

    numerator /= divisor;
    denominator /= divisor;

    return `${Math.floor(numerator)}/${Math.floor(denominator)}`;
  }

  convertImperialToMetric(value: string | number): string {
    return (Number(value) * 25.4).toFixed(3);
  }

  convertMetricToImperial(value: string | number): string {
    return (Number(value) / 25.4).toFixed(2);
  }

  roundNumber(number: number, decimalPlaces: number) {
    return parseFloat(`${Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)}`).toFixed(decimalPlaces);
  }

  round(value: string | number, minimumFractionDigits: number, maximumFractionDigits: number) {
    const formattedValue = value.toLocaleString('en', {
      useGrouping: false,
      minimumFractionDigits,
      maximumFractionDigits,
    });
    return formattedValue;
  }

  getSelectLabelByValue(value: string | number | null, selectOptions: SelectOptionType[]): string | number | null {
    let selectLabel = value;
    for (const option of selectOptions) {
      if (option.value === value) {
        selectLabel = option.label;
        break;
      }
    }
    return selectLabel;
  }

  setCookie(name: string, value: string, hours = 99999) {
    const now = new Date();
    now.setTime(now.getTime() + hours * 3600 * 1000);

    document.cookie = `${name}=${value}; expires=${now.toString()}; path=/; domain=${window.location.hostname};`;
  }

  readCookie(name: string) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i].trim();
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  removeCookie(name: string) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;
  }

  setToLSWithExpiry(key: string, value: any, hours: number = 24) {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + hours * 60 * 60 * 1000,
    };

    LocalStorageHelper.setItem(key, JSON.stringify(item));
  }

  getFromLSWithExpiry(key: string) {
    const itemStr = LocalStorageHelper.getItem(key);

    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > item.expiry) {
      LocalStorageHelper.removeItem(key);
      
      return null;
    }

    return item.value;
  }

  dynamicModalLogic() {
    const dynamicModal = document.querySelector('#dynamic-modal');

    if (dynamicModal !== null) {
      const dynamicModalCloseElements = ['.dynamic-modal-backdrop', '.dynamic-modal-close'];
      dynamicModalCloseElements.forEach((element) => {
        dynamicModal.querySelector(element).addEventListener('click', () => {
          dynamicModal.classList.remove('opacity-100', 'pointer-events-auto');

          dynamicModal.querySelector('iframe')?.setAttribute('src', '');
        });
      });
    }
  }

  roundToDecimals(value: number, decimals: number): number {
    return Number(value.toFixed(decimals));
  }

  capitalizeSentence(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  makeParentRowNotRelative(elementSelector: string) {
    const element = document.querySelector(elementSelector);

    element?.closest('.column')?.style.setProperty('position', 'static', 'important');
    element?.closest('.row')?.style.setProperty('position', 'static', 'important');
    element?.closest('.row')?.style.setProperty('display', 'block', 'important');
  }

  numberGrowingAnimation(
    from: number,
    to: number,
    options: {
      duration?: number;
      decimals?: number;
      easing?: (t: number) => number;
      onUpdate: (val: number) => void;
      onFinish?: () => void;
    }
  ) {
    const { duration = 1000, decimals = 0, easing = (t: number) => 1 - (1 - t) * (1 - t), onUpdate, onFinish } = options;

    const start = performance.now();
    const diff = to - from;

    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = easing(t);
      const value = Number((from + diff * eased).toFixed(decimals));
      onUpdate(value);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        onFinish?.();
      }
    };

    requestAnimationFrame(step);
  }

  copyToClipboard(text: string) {
    const textareaTempElement = document.createElement('textarea');
    textareaTempElement.style.position = 'fixed';
    textareaTempElement.style.opacity = '0';
    textareaTempElement.value = text;
    document.body.appendChild(textareaTempElement);
    textareaTempElement.select();
    document.execCommand('copy');
    document.body.removeChild(textareaTempElement);
  }

  debounce (fn, wait) {
		let t = null;
		return (...args) => {
			if (t) clearTimeout(t);
			t = setTimeout(() => fn(...args), wait);
		};
	};
}

export default new GlobalHelper();
